import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLoginMutation } from "@/hooks/auth";
import Link from "next/link";

// Labels for text
const labels = {
  loginTitle: "Prijava",
  loginDescription: "Unesite svoj email kako biste se prijavili na svoj račun",
  emailLabel: "Email",
  passwordLabel: "Lozinka",
  registerAccount: "Registrirajte račun",
  loginButton: "Prijavite se",
};

// Define the schema for validation
const loginSchema = z.object({
  email: z.string().email("Unesite važeću email adresu"), // Enter a valid email address
  password: z.string().min(6, "Lozinka mora imati najmanje 6 znakova"), // Password must be at least 6 characters
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema), // Use Zod for validation
    mode: "onSubmit",
    shouldUseNativeValidation: false,
  });

  // Mutation for logging in
  const loginMutation = useLoginMutation({
    onSuccess: () => {
      console.log("Login successful");

      window.location.href = "/";
    },
  });

  const onSubmit = (data: LoginFormValues) => {
    loginMutation.mutate(data);
  };

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{labels.loginTitle}</CardTitle>
          <CardDescription>{labels.loginDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">{labels.emailLabel}</Label>
                <Input id="email" type="email" {...register("email")} />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">{labels.passwordLabel}</Label>
                  <Link
                    href="/register"
                    className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  >
                    {labels.registerAccount}
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  {...register("password")}
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.password.message}
                  </p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full"
                isLoading={loginMutation.isPending}
              >
                {labels.loginButton}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
